import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { PaymentItem } from "~/projects/crm/types";

export const usePaymentItemStore = definePiniaStore("usePaymentItemStore", {
  state: () => ({
    _loading: false,
    _tableData: [] as PaymentItem[],
    _pagination: {} as Pagination,
    _filterData: {} as Record<string, any>,
    showCreateModal: false,
    showEditModal: false,
    _selectedItem: {} as PaymentItem,
  }),
  getters: {
    tableData: (state) => state._tableData,
    pagination: (state) => state._pagination,
    selectedItem: (state) => state._selectedItem,
  },
  actions: {
    setFilterData(filterData: Record<string, any>) {
      this._filterData = filterData;
    },

    async getPaymentItems(pageNo = 1) {
      this._loading = true;

      /*
       * Built with URLSearchParams rather than by concatenation.
       *
       * The old loop pasted every key in raw, which sent `&search=` for a
       * filter the agent had cleared — harmless against `filled()` today, but
       * only by luck — and would corrupt the query string outright for any
       * value containing `&`, `#` or a space. Payment item names are free text,
       * so that is a matter of time rather than of chance.
       */
      const params = new URLSearchParams({ page: String(pageNo) });

      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null || value === undefined || value === "") continue;
        params.set(key, String(value));
      }

      const url = `crm/product-payment-items?${params.toString()}`;
      const { response, error } = await getRequest<
        TableDataWithPagination<PaymentItem>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this._tableData = data;
        this._pagination = pagination;
      }
      this._loading = false;
    },

    async getAll() {
      const { response, error } = await getRequest<PaymentItem[]>(
        `crm/product-payment-items?all=true`,
      );
      if (!error) return response.data;
      return [];
    },

    async getAllByProduct(productId: number) {
      const { response, error } = await getRequest<PaymentItem[]>(
        `products/${productId}/payment-items`,
      );
      if (!error) return response.data;
      return [];
    },

    async getAllUnassigned() {
      const { response, error } = await getRequest<PaymentItem[]>(
        `crm/product-payment-items?unassigned=true&all=true`,
      );
      if (!error) return response.data;
      return [];
    },

    async createPaymentItem(data: Partial<PaymentItem>) {
      const { response, error } = await postRequest<PaymentItem>(
        "crm/product-payment-items",
        data,
      );
      if (!error) return response.data;
      return null;
    },

    async updatePaymentItem(data: PaymentItem) {
      const { response, error } = await putRequest<PaymentItem>(
        `crm/product-payment-items/${data.id}`,
        data,
      );
      if (!error) return response.data;
      return null;
    },

    async attachToProduct(productId: number, paymentItemId: number, price: number, isRequired: boolean = false) {
      const { response, error } = await postRequest<PaymentItem>(
        `products/${productId}/payment-items`,
        { payment_item_id: paymentItemId, price, is_required: isRequired },
      );
      if (!error) return response.data;
      return null;
    },

    async updatePivotPrice(productId: number, paymentItemId: number, price: number) {
      const { response, error } = await putRequest<PaymentItem>(
        `products/${productId}/payment-items/${paymentItemId}`,
        { price },
      );
      if (!error) return response.data;
      return null;
    },

    async detachFromProduct(productId: number, paymentItemId: number) {
      return await deleteRequest(
        `products/${productId}/payment-items/${paymentItemId}`,
      );
    },

    async deletePaymentItem(id: number) {
      return await deleteRequest(`crm/product-payment-items/${id}`);
    },
  },
});
